import {castDraft, Draft} from 'immer';
import {last} from 'lodash';
import {useReducer} from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {
  PreviewBuilderModel,
  PreviewerLinkTypesEnum,
  PreviewerModel,
} from '../../../models/api/previewerModel';
import {buildConditionTypeFromExpression, buildRuleCondition} from '../../rules/ruleConditionHelpers';
import {decorateWithClientId} from '../commons/clientIdModel';
import {CustomFields} from '../commons/customFieldsModel';
import {ConditionsStateActions, useConditionsState} from '../conditions/conditionsState';
import {ExpressionStateActions, useExpressionState} from '../expressions/expressionState';
import {PreviewerEditorActionTypesEnum} from './previewerEditorActions';
import {parsePreviewerDryRunError} from './previewerEditorDryRunErrors';
import {previewerEditorReducer} from './previewerEditorReducer';
import {PreviewerEditorState} from './previewerEditorState';

export interface PreviewerEditorStateActions extends ExpressionStateActions, ConditionsStateActions {
  onUpdateLinkType: HandlerOf<PreviewerLinkTypesEnum>;
  onUpdateDomain: HandlerOf<string>;
  onUpdateDescription: HandlerOf<string>;
  onUpdateExampleUrl: HandlerOf<string | undefined>;
  onStartRequestExpression: Handler;
  onUpdatePreviewBuilder: HandlerOf<PreviewBuilderModel>;
  onExecDryRun: Handler;
}

export function usePreviewerEditorState(
  initialPreviewer: PreviewerModel | undefined,
  customFields: CustomFields,
  application: LocalApplicationModel,
): {previewerEditorState: PreviewerEditorState} & PreviewerEditorStateActions {
  const [previewerEditorState, dispatch] = useReducer(
    previewerEditorReducer,
    PreviewerEditorState.createInitialState(initialPreviewer, customFields),
  );

  const onUpdateLinkType = (linkType: PreviewerLinkTypesEnum) => {
    dispatch({type: PreviewerEditorActionTypesEnum.UPDATE_LINK_TYPE, linkType});
  };

  const onUpdateDomain = (domain: string) => {
    dispatch({type: PreviewerEditorActionTypesEnum.UPDATE_DOMAIN, domain});
  };

  const onUpdateDescription = (description: string) => {
    dispatch({type: PreviewerEditorActionTypesEnum.UPDATE_DESCRIPTION, description});
  };

  const onUpdateExampleUrl = (exampleUrl: string | undefined) => {
    dispatch({type: PreviewerEditorActionTypesEnum.UPDATE_EXAMPLE_URL, exampleUrl});
  };

  const onStartRequestExpression = () => {
    dispatch({type: PreviewerEditorActionTypesEnum.START_REQUEST_EXPRESSION});
  };

  const onUpdatePreviewBuilder = (previewBuilder: PreviewBuilderModel) => {
    dispatch({type: PreviewerEditorActionTypesEnum.UPDATE_PREVIEW_BUILDER, previewBuilder});
  };

  const expressionState = useExpressionState(
    {
      onAddExpressionValidator: addVariableDefinedCondition,
      makeOnDeleteExpressionValidator: undefined,
      makeOnAddAtomValidator: undefined,
      makeOnUpdateAtomValidator: undefined,
      makeOnDeleteAtomValidator: undefined,
    },
    previewerEditorState,
    dispatch,
  );

  const conditionsState = useConditionsState(dispatch);

  const onExecDryRun = async () => {
    const dryRunEndpoint = `${application.url}/previewers/dryrun`;
    const {exampleUrl} = previewerEditorState.previewerState;

    if (!exampleUrl) {
      return;
    }

    try {
      await expressionState.onUpdateDryRun(dryRunEndpoint, {url: exampleUrl});
    } catch (err: any) {
      expressionState.onDryRunError(parsePreviewerDryRunError(err));
    }
  };

  return {
    previewerEditorState,

    onUpdateLinkType,
    onUpdateDomain,
    onUpdateDescription,
    onUpdateExampleUrl,
    onStartRequestExpression,
    onUpdatePreviewBuilder,
    onExecDryRun,

    ...expressionState,
    ...conditionsState,
  };
}

// A previewer needs at least one condition to save. Also, you usually expect expressions to be defined.
// This will automatically add an "id defined" conditions whenever a new expression is added.
function addVariableDefinedCondition(state: Draft<PreviewerEditorState>) {
  const {previewerState, expressions} = state;

  const addedExpression = last(expressions);
  if (!addedExpression) {
    return;
  }

  const condition = decorateWithClientId(
    buildRuleCondition(buildConditionTypeFromExpression(addedExpression), {
      expression: addedExpression.id,
      comparator: 'is_defined',
    }),
  );

  if (!previewerState.conditions) {
    previewerState.conditions = [];
  }

  previewerState.conditions.push([castDraft(condition)]);
}
